/**
 * `rerun-pipeline-contract` — reset a contract that failed **before any AI call**
 * or whose latest AI action was a **provider / request failure** (never a
 * refusal) so the next `run-pipeline` dispatch converts it again · CLM-79
 * (completes R-70-1; D-79-1…D-79-8, adopted 11 Sep 2026 per recommendation;
 * Gerry may overturn), widened by CLM-80 D-80-2 (12 Sep 2026).
 *
 * CLM-70 shipped re-run only for the retryable branch (a failure left at
 * `Pseudonymised` is re-picked by the next run). A contract that fails at the
 * conversion quality gate or in pseudonymisation is marked `ExtractionFailed`
 * with **no** AI audit row — the failure happened before the boundary — and
 * `pendingIntakeQuery` never picks it again. This endpoint is the reset.
 *
 * ## What the reset is (D-79-3, D-79-4)
 *
 * One scoped `UPDATE clm.contract`: `processing_status` becomes
 * `manual_entry_pending` when `processing_restricted`, else `PendingExtraction`
 * (the two values `insertContractQuery` writes); `data_trust` becomes
 * `PendingExtraction`; `full_text`, `page_count`, `conversion_method`,
 * `converted_utc` and `pseudonym_map_version` are cleared. Clearing is not
 * optional: `storeConversionQuery` is guarded on `conversion_method IS NULL`,
 * so a reset that left it set would re-convert (billed per page) and store
 * nothing.
 *
 * ## The audit row is the trigger's (D-79-1)
 *
 * `tg_contract_audit` (0046) writes exactly one `Update` row for the reset,
 * with the changed keys and the session actor (`source_component =
 * 'pipeline-rerun'`, D-79-5). The endpoint writes **no** audit statement of its
 * own — a second row for one act is what rule 7 forbids. The response carries
 * that row read back in the same transaction (D-79-8).
 *
 * ## Refusals
 *
 * 409 `conflict`, the reason in `field_errors[0].message` as one of
 * {@link RERUN_REFUSALS}: `retryable_pending` (the row is at `Pseudonymised` —
 * the next run resumes it), `failed_after_ai` (the latest AI row is a refusal —
 * routed to human review, never a re-serve, rule 6 — or a success; a latest
 * `AIExtractionFailure` is admitted, D-80-2), `not_failed`,
 * `no_ledger_row` (the runner inner-joins the ingest ledger, so a reset would
 * never be picked up). Out of scope or absent ⇒ 404.
 *
 * `reason` is validated and logged, not stored (D-79-6): neither `audit_log`
 * nor `contract` has a note column, and this story ships no DDL.
 */
import { z } from 'zod';
import { ContractKeySchema, IsoTimestampUtcSchema } from '../primitives.js';
import { AuditReceiptSchema } from '../provenance.js';
import { ProcessingStatusSchema } from '../vocabulary.js';
/** The four 409 reasons; each rides in `field_errors[0].message` with `path: 'contract_key'`. */
export const RERUN_REFUSALS = ['retryable_pending', 'failed_after_ai', 'not_failed', 'no_ledger_row'];
export const RERUN_REASON_MAX_LENGTH = 1000;
export const RerunPipelineContractRequestSchema = z
    .object({
    contract_key: ContractKeySchema,
    /** Optional; validated (1..1000) and written to the structured log beside the audit_id — never persisted (D-79-6). */
    reason: z.string().min(1).max(RERUN_REASON_MAX_LENGTH).optional(),
})
    .strict();
export const RerunPipelineContractResponseSchema = z
    .object({
    contract_key: ContractKeySchema,
    /** What the reset wrote: PendingExtraction, or manual_entry_pending when restricted. */
    processing_status: ProcessingStatusSchema,
    rerun_utc: IsoTimestampUtcSchema,
    /** The `Update` row `tg_contract_audit` wrote, read back in the same transaction (D-79-8). */
    audit: AuditReceiptSchema,
})
    .strict();
//# sourceMappingURL=pipeline-rerun.js.map